import { useEffect, useState } from 'react';
import type { ArchitectStandupNote } from '../types';
import {
  latestArchitectStandupByProject,
  subscribeRecentArchitectStandupNotes,
} from '../utils/architectStandupService';
import type { ProjectStandupLatest } from '../components/pm/ProjectStandupCell';

export function useArchitectStandupLatestByProject(enabled: boolean): Map<string, ProjectStandupLatest> {
  const [byProject, setByProject] = useState<Map<string, ProjectStandupLatest>>(() => new Map());

  useEffect(() => {
    if (!enabled) {
      setByProject(new Map());
      return;
    }
    return subscribeRecentArchitectStandupNotes(notes => {
      const latest = latestArchitectStandupByProject(notes);
      const next = new Map<string, ProjectStandupLatest>();
      latest.forEach((note: ArchitectStandupNote, projectId) => {
        next.set(projectId, {
          text: note.text,
          audioUrl: note.audioUrl,
          updatedAt: note.createdAt,
          updatedByName: note.createdByName,
        });
      });
      setByProject(next);
    });
  }, [enabled]);

  return byProject;
}

/** Per-project latest for architect standup board (scoped listener). */
export function useArchitectStandupLatestForProjects(
  projectIds: string[],
  notesByProjectFromGlobal?: Map<string, ProjectStandupLatest>,
): Map<string, ProjectStandupLatest> {
  const [local, setLocal] = useState<Map<string, ProjectStandupLatest>>(() => new Map());

  useEffect(() => {
    if (notesByProjectFromGlobal) {
      setLocal(notesByProjectFromGlobal);
    }
  }, [notesByProjectFromGlobal]);

  useEffect(() => {
    if (notesByProjectFromGlobal || projectIds.length === 0) return;
    return subscribeRecentArchitectStandupNotes(notes => {
      const latest = latestArchitectStandupByProject(notes);
      const idSet = new Set(projectIds);
      const next = new Map<string, ProjectStandupLatest>();
      latest.forEach((note, projectId) => {
        if (!idSet.has(projectId)) return;
        next.set(projectId, {
          text: note.text,
          audioUrl: note.audioUrl,
          updatedAt: note.createdAt,
          updatedByName: note.createdByName,
        });
      });
      setLocal(next);
    });
  }, [notesByProjectFromGlobal, projectIds]);

  return notesByProjectFromGlobal ?? local;
}
